import {type BucketedLine} from '../../types';
import {
  applyBackendLinesDelta,
  applyDelta,
  applyGroupedBackendLinesDelta,
  applyGroupedDelta,
} from './applyDeltas';
import {
  doBackendLinesApiQuery,
  doBackendLinesApiQueryByInternalId,
  doBucketedApiQuery,
  doBucketedApiQueryByInternalId,
  doGroupedBackendLinesApiQuery,
  doGroupedBucketedApiQuery,
  doGroupedBucketedApiQueryByInternalId,
} from './execution';
import {mergeBaselineAndPinnedRuns} from './mergeBaselineAndPinned';
import {postProcessGroupedRuns, postProcessRuns} from './postProcessRuns';
import {type QueryVars} from './queryVars';
import {
  type BackendLinesQueryResult,
  type BackendLinesQueryRuns,
  type BackendLinesQueryRunsDeltas,
  type BaseQueryBehavior,
  type BucketedQueryResult,
  type BucketedQueryRuns,
  type BucketedQueryRunsDeltas,
  type GroupedBackendLinesQueryResult,
  type GroupedBackendLinesQueryRuns,
  type GroupedBucketedQueryResult,
  type GroupedBucketedQueryRuns,
  type RunFlags,
} from './types';
import {extractDeltasInfo, isAggregatedRuns} from './utils';

type BackendLinesQueryBehaviorType = BaseQueryBehavior<
  BackendLinesQueryResult,
  BackendLinesQueryRuns
>;

type GroupedBackendLinesQueryBehaviorType = BaseQueryBehavior<
  GroupedBackendLinesQueryResult,
  GroupedBackendLinesQueryRuns
>;

/**
 * Type for deltas object augmented with runFlags.
 * runFlags is added at runtime by mergeBaselineAndPinnedRuns.
 */
type DeltasWithRunFlags = {runFlags?: RunFlags};

function applyBackendLinesDeltaFn(vars: {
  currentRunsById: Record<string, BucketedLine>;
  runs: BackendLinesQueryRuns | null | undefined;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const {deltas, order, runFlags} = extractDeltasInfo(vars.runs);
  return applyBackendLinesDelta({
    currentRunsById: vars.currentRunsById,
    delta: deltas,
    order,
    runFlags,
  });
}

function applyBucketedDeltaFn(vars: {
  currentRunsById: Record<string, BucketedLine>;
  runs: BucketedQueryRuns | null | undefined;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const {deltas, order, runFlags} = extractDeltasInfo(vars.runs);
  return applyDelta({
    currentRunsById: vars.currentRunsById,
    order,
    delta: deltas,
    specs: vars.specs,
    runFlags,
  });
}

function unpackBackendLinesRuns(result: BackendLinesQueryResult) {
  return result?.data?.project?.runs;
}

function unpackBucketedRuns(result: BucketedQueryResult) {
  return result?.data?.project?.runs;
}

function unpackGroupedBackendLinesRuns(result: GroupedBackendLinesQueryResult) {
  return result?.data?.project?.groupedRuns;
}

function unpackGroupedBucketedRuns(result: GroupedBucketedQueryResult) {
  return result?.data?.project?.groupedRuns;
}

export const BackendLinesQueryBehavior: BackendLinesQueryBehaviorType = {
  queryName: 'BackendLinesQueryBehavior',
  query: doBackendLinesApiQuery,
  mergeBaselineAndPinned: mergeBaselineAndPinnedRuns,
  unpack: unpackBackendLinesRuns,
  postProcess: null,
  applyDeltaFn: applyBackendLinesDeltaFn,
};

export const BackendLinesIdQueryBehavior: BackendLinesQueryBehaviorType = {
  queryName: 'BackendLinesIdQueryBehavior',
  query: doBackendLinesApiQueryByInternalId,
  mergeBaselineAndPinned: mergeBaselineAndPinnedRuns,
  unpack: unpackBackendLinesRuns,
  postProcess: null,
  applyDeltaFn: applyBackendLinesDeltaFn,
};

export const GroupedBackendLinesQueryBehavior: GroupedBackendLinesQueryBehaviorType =
  {
    queryName: 'GroupedBackendLinesQueryBehavior',
    query: doGroupedBackendLinesApiQuery,
    mergeBaselineAndPinned: null,
    unpack: unpackGroupedBackendLinesRuns,
    postProcess: null,
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBackendLinesQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      /**
       * The Project.GroupedRuns resolver returns GroupedRuns if it's a supported use case or Runs if it's not. If it is, we can use the grouped delta function. Otherwise we fall back to the same behavior as the non-grouped BackendLinesQueryBehavior (applyBackendLinesDelta), since it will have returned non-aggregated line data.
       */
      const runs = vars.runs;
      const order = vars.runs?.deltas?.order ?? [];
      const runFlags = (vars.runs?.deltas as DeltasWithRunFlags | undefined)
        ?.runFlags;
      const delta = vars.runs?.deltas?.delta ?? [];

      if (isAggregatedRuns(runs)) {
        return applyGroupedBackendLinesDelta({
          currentRunsById: vars.currentRunsById,
          order,
          delta,
        });
      }

      return applyBackendLinesDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta: delta as BackendLinesQueryRunsDeltas,
        runFlags,
      });
    },
  };

type BucketedQueryBehaviorType = BaseQueryBehavior<
  BucketedQueryResult,
  BucketedQueryRuns
>;

export const BucketedQueryBehavior: BucketedQueryBehaviorType = {
  queryName: 'BucketedQueryBehavior',
  query: doBucketedApiQuery,
  mergeBaselineAndPinned: mergeBaselineAndPinnedRuns,
  unpack: unpackBucketedRuns,
  postProcess: postProcessRuns,
  applyDeltaFn: applyBucketedDeltaFn,
};

export const BucketedQueryWithInternalIdBehavior: BucketedQueryBehaviorType = {
  queryName: 'BucketedQueryWithInternalIdBehavior',
  query: doBucketedApiQueryByInternalId,
  mergeBaselineAndPinned: mergeBaselineAndPinnedRuns,
  unpack: unpackBucketedRuns,
  postProcess: postProcessRuns,
  applyDeltaFn: applyBucketedDeltaFn,
};

type GroupedQueryBehaviorType = BaseQueryBehavior<
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns
>;

export const GroupedBucketedQueryBehavior: GroupedQueryBehaviorType = {
  queryName: 'GroupedBucketedQueryBehavior',
  query: doGroupedBucketedApiQuery,
  mergeBaselineAndPinned: null,
  unpack: unpackGroupedBucketedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs as BucketedQueryRuns);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
        specs: vars.specs,
      });
    }

    // After the isAggregatedRuns check above, we know this is a RunConnection (not GroupedRunConnection),
    // but TypeScript can't narrow runs through the type guard, so we cast the delta type.
    const delta = (runs?.deltas?.delta ?? []) as BucketedQueryRunsDeltas;
    const order = runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta,
      specs: vars.specs,
    });
  },
};

export const GroupedBucketedQueryWithInternalIdBehavior: GroupedQueryBehaviorType =
  {
    queryName: 'GroupedBucketedQueryWithInternalIdBehavior',
    query: doGroupedBucketedApiQueryByInternalId,
    mergeBaselineAndPinned: null,
    unpack: unpackGroupedBucketedRuns,
    postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
      if (runs?.__typename === 'GroupedRunConnection') {
        return postProcessGroupedRuns(runs);
      }
      return postProcessRuns(runs as BucketedQueryRuns);
    },
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBucketedQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      const runs = vars.runs;
      if (isAggregatedRuns(runs)) {
        return applyGroupedDelta({
          currentRunsById: vars.currentRunsById,
          order: runs.deltas?.order ?? [],
          delta: runs.deltas?.delta ?? [],
          specs: vars.specs,
        });
      }

      // After the isAggregatedRuns check above, we know this is a RunConnection (not GroupedRunConnection),
      // but TypeScript can't narrow runs through the type guard, so we cast the delta type.
      const delta = (runs?.deltas?.delta ?? []) as BucketedQueryRunsDeltas;
      const order = runs?.deltas?.order ?? [];
      return applyDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta,
        specs: vars.specs,
      });
    },
  };
